
import { pick } from 'lodash-es'
import { defineComponent, shallowRef, computed, proxyRefs, PropType } from 'vue'
import { refDebounced } from '@vueuse/core'

// Composables
import { useTitle } from '@vueuse/core'
import { useRoute } from '@/use/router'
import { UseDateRange } from '@/use/date-range'
import { useUser } from '@/org/use-users'
import { useSystems, System } from '@/tracing/system/use-systems'
import { useUql, createQueryEditor, provideQueryStore, useQueryStore } from '@/use/uql'

// Components
import TracingPlaceholder from '@/tracing/TracingPlaceholder.vue'
import DateRangePicker from '@/components/date/DateRangePicker.vue'
import SystemPicker from '@/tracing/system/SystemPicker.vue'
import SystemGroupPicker from '@/tracing/system/SystemGroupPicker.vue'
import SavedViews from '@/tracing/views/SavedViews.vue'
import UptraceQuery from '@/components/UptraceQuery.vue'
import SpanQueryBuilder from '@/tracing/query/SpanQueryBuilder.vue'
import QuickSearch from '@/components/QuickSearch.vue'

// Misc
import { SystemName, AttrKey } from '@/models/otel'

export default defineComponent({
  name: 'Tracing',
  components: {
    TracingPlaceholder,
    DateRangePicker,
    SystemPicker,
    SystemGroupPicker,
    SavedViews,
    UptraceQuery,
    SpanQueryBuilder,
    QuickSearch,
  },

  props: {
    dateRange: {
      type: Object as PropType<UseDateRange>,
      required: true,
    },
  },

  setup(props) {
    useTitle('Explore spans')

    const user = useUser()

    const searchInput = shallowRef('')
    const debouncedSearchInput = refDebounced(searchInput, 600)

    const uql = useUql()
    provideQueryStore(useQueryStore(uql))

    const systems = useSystems(() => {
      return {
        ...props.dateRange.axiosParams(),
        query: uql.whereQuery,
      }
    })

    const systemItems = shallowRef<System[]>([])

    const axiosParams = computed(() => {
      const params: Record<string, any> = {
        ...props.dateRange.axiosParams(),
        ...systems.axiosParams(),
        ...uql.axiosParams(),
      }
      if (debouncedSearchInput.value) {
        params.search = debouncedSearchInput.value
      }
      return params
    })

    const spanListName = computed(() => {
      switch (systems.groupName) {
        case SystemName.LogAll:
          return 'Logs'
        case SystemName.EventsAll:
          return 'Events'
        default:
          return 'Spans'
      }
    })

    function resetQuery(clear = false) {
      uql.query = createQueryEditor()
        .exploreAttr(AttrKey.spanGroupId, systems.isSpan)
        .add(clear ? '' : uql.whereQuery)
        .toString()
    }

    return {
      user,
      systems,
      systemItems,

      uql,
      axiosParams,
      searchInput,
      spanListName,

      routes: useRoutes(),

      resetQuery,
    }
  },
})

function useRoutes() {
  const route = useRoute()

  const groupList = computed(() => {
    return routeFor('SpanGroupList')
  })

  const spanList = computed(() => {
    return routeFor('SpanList')
  })

  const timeseries = computed(() => {
    return routeFor('SpanTimeseries')
  })

  function routeFor(routeName: string) {
    return {
      name: routeName,
      query: pick(route.value.query, ['system', 'query', 'time_gte', 'time_dur']),
    }
  }

  return proxyRefs({
    groupList,
    spanList,
    timeseries,
  })
}
